import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

const KEY = 'lifehub.theme';
const listeners = new Set<() => void>();

function systemTheme(): Theme {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Le thème choisi est une simple préférence d'affichage : jamais de donnée sensible dans le stockage local.
function stored(): Theme | null {
  try {
    const value = window.localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

let current: Theme = typeof window === 'undefined' ? 'light' : (stored() ?? systemTheme());

export function applyTheme(theme: Theme): void {
  current = theme;
  document.documentElement.setAttribute('data-theme', theme);
  try {
    window.localStorage.setItem(KEY, theme);
  } catch {
    // stockage indisponible (navigation privée) : le thème reste valable pour la session
  }
  listeners.forEach((l) => l());
}

export function initTheme(): void {
  document.documentElement.setAttribute('data-theme', current);
}

export function useTheme(): [Theme, (theme: Theme) => void] {
  const theme = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => 'light' as Theme,
  );
  const set = useCallback((next: Theme) => applyTheme(next), []);
  return [theme, set];
}
